// NOTE(dev): When modifying these schemas, also update the corresponding
// GraphQL types in api/src/entities/NegotiationStrategy/NegotiationStrategyConfigTypes.ts
import { z } from 'zod';
import { RatingMethod } from './pricingStrategy';

export enum NegotiationStrategyType {
  AB_TEST = 'AB_TEST',
  ACCEPT_UP_TO_MAX = 'ACCEPT_UP_TO_MAX',
  ALWAYS_ACCEPT = 'ALWAYS_ACCEPT',
  ALWAYS_DECLINE = 'ALWAYS_DECLINE',
  CONDITIONAL = 'CONDITIONAL',
  ESCALATION = 'ESCALATION',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  NEVER_BUDGE = 'NEVER_BUDGE',
  PERCENTAGE_BASED = 'PERCENTAGE_BASED',
  SMART_PRICING = 'SMART_PRICING',
}

export const HotLoadConditionSchema = z.object({
  offerCountThreshold: z.number().min(1),
  timeWindowMinutes: z.number().min(1).optional(),
});

export type HotLoadCondition = z.infer<typeof HotLoadConditionSchema>;

export const PickupDateWithinSchema = z.union([
  z.number().min(0),
  z.enum(['today', 'this_week']),
]);

export type PickupDateWithin = z.infer<typeof PickupDateWithinSchema>;

const NegotiationStrategyConditionsSchema = z.object({
  carrierOfferOverMax: z.boolean().optional(),
  offerRangePercentAtLeast: z.number().min(0).max(100).optional(),
  carrierStoppedBudging: z.boolean().optional(),
  commodityPatterns: z.array(z.string()).optional(),
  equipmentTypes: z.array(z.string()).optional(),
  carrierEquipmentTypes: z.array(z.string()).optional(),
  targetPriceAtLeast: z.number().optional(),
  targetPriceAtMost: z.number().optional(),
  maxPriceAtLeast: z.number().optional(),
  maxPriceAtMost: z.number().optional(),
  hotLoad: HotLoadConditionSchema.optional(),
  inquiryCountAtLeast: z.number().min(0).optional(),
  inquiryCountAtMost: z.number().min(0).optional(),
  inquiryTimeWindowMinutes: z.number().min(1).optional(),
  offerCountAtLeast: z.number().min(0).optional(),
  offerCountAtMost: z.number().min(0).optional(),
  offerTimeWindowMinutes: z.number().min(1).optional(),
  hasAnyOfferAbove: z.number().optional(),
  hasAnyOfferBelow: z.number().optional(),
  averageOfferAbove: z.number().optional(),
  averageOfferBelow: z.number().optional(),
  transferCountAtLeast: z.number().min(0).optional(),
  transferCountAtMost: z.number().min(0).optional(),
  transferTimeWindowMinutes: z.number().min(1).optional(),
  hasRateGuidanceForMethod: z.nativeEnum(RatingMethod).optional(),
  rateGuidanceAtLeast: z.number().optional(),
  rateGuidanceAtMost: z.number().optional(),
  pickupDateWithin: PickupDateWithinSchema.optional(),
  pickupWithinHours: z.number().min(0).optional(),
  pickupEndDateWithin: PickupDateWithinSchema.optional(),
  pickupEndWithinHours: z.number().min(0).optional(),
  rejectionCountAtLeast: z.number().min(0).optional(),
  rejectionCountAtMost: z.number().min(0).optional(),
  offerGreaterThanMaxPlus: z.number().optional(),
});

export type NegotiationStrategyConditions = z.infer<
  typeof NegotiationStrategyConditionsSchema
>;

export const AutoRejectThresholdSchema = z.object({
  autoRejectThreshold: z.number().min(0).optional(),
  autoRejectThresholdPercent: z.number().min(0).max(100).optional(),
});

export const TargetBufferSchema = z.object({
  targetBuffer: z.number().min(0).optional(),
  targetBufferPercent: z.number().min(0).max(100).optional(),
});
export type TargetBufferConfig = z.infer<typeof TargetBufferSchema>;
export const IncrementTierSchema = z.object({
  carrierOfferOverMax: z.boolean().optional(),
  targetPriceAtLeast: z.number().optional(),
  targetPriceAtMost: z.number().optional(),
  incrementAmount: z.number(),
});
export type IncrementTier = z.infer<typeof IncrementTierSchema>;

export type AutoRejectThresholdConfig = z.infer<
  typeof AutoRejectThresholdSchema
>;

export const FirstOfferBelowTargetCounterSchema = z.object({
  firstOfferBelowTargetCounterPercent: z.number().min(0).max(100).optional(),
  firstOfferBelowTargetCounterDollars: z.number().min(0).optional(),
});
export type FirstOfferBelowTargetCounterConfig = z.infer<
  typeof FirstOfferBelowTargetCounterSchema
>;

export enum CarrierNoBudgeBehavior {
  DONT_BUDGE = 'DONT_BUDGE',
  NEGOTIATE = 'NEGOTIATE',
}

export const CarrierNoBudgeBehaviorSchema = z.object({
  carrierNoBudgeBehavior: z.nativeEnum(CarrierNoBudgeBehavior).optional(),
});
export type CarrierNoBudgeBehaviorConfig = z.infer<
  typeof CarrierNoBudgeBehaviorSchema
>;

export const FixedAmountConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.FIXED_AMOUNT),
    incrementAmount: z.number().default(25),
    incrementTiers: z.array(IncrementTierSchema).optional(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type FixedAmountConfig = z.infer<typeof FixedAmountConfigSchema>;

export const PercentageBasedConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.PERCENTAGE_BASED),
    incrementPercentage: z.number().min(0).max(100).default(10),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type PercentageBasedConfig = z.infer<typeof PercentageBasedConfigSchema>;

export const NeverBudgeConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.NEVER_BUDGE),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type NeverBudgeConfig = z.infer<typeof NeverBudgeConfigSchema>;

export enum EscalationChannel {
  AGENT_HANDLED = 'AGENT_HANDLED',
  SLACK = 'SLACK',
  SOP = 'SOP',
  VOICE = 'VOICE',
}

export const EscalationConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.ESCALATION),
    escalationThreshold: z.number().min(0).default(1),
    initialIncrementAmount: z.number().default(25),
    maxEscalationOverage: z.number().min(0).optional(),
    timeoutSeconds: z.number().int().min(1).default(120),
    escalationMessage: z.string().optional(),
    includeFields: z.array(z.string()).optional(),
    escalationChannel: z.nativeEnum(EscalationChannel),
    holdMessage: z.string().optional(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type EscalationConfig = z.infer<typeof EscalationConfigSchema>;

export const ABTestVariantSchema = z.object({
  strategyId: z.string().uuid(),
  weight: z.number().min(0).max(100),
});
export type ABTestVariant = z.infer<typeof ABTestVariantSchema>;

export enum ABTestAssignmentMethod {
  SHIPMENT_HASH = 'shipment_hash',
  CONVERSATION_HASH = 'conversation_hash',
  RANDOM = 'random',
}

export const ABTestConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.AB_TEST),
    variants: z.array(ABTestVariantSchema).min(2),
    assignmentMethod: z
      .nativeEnum(ABTestAssignmentMethod)
      .default(ABTestAssignmentMethod.SHIPMENT_HASH),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type ABTestConfig = z.infer<typeof ABTestConfigSchema>;

export const ConditionalRuleSchema = z.object({
  conditions: NegotiationStrategyConditionsSchema,
  strategyId: z.string().uuid(),
});
export type ConditionalRule = z.infer<typeof ConditionalRuleSchema>;

export const ConditionalConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.CONDITIONAL),
    rules: z.array(ConditionalRuleSchema).min(1),
    defaultStrategyId: z.string().uuid(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type ConditionalConfig = z.infer<typeof ConditionalConfigSchema>;

export const AcceptUpToMaxConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.ACCEPT_UP_TO_MAX),
    fallbackStrategyId: z.string().uuid(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type AcceptUpToMaxConfig = z.infer<typeof AcceptUpToMaxConfigSchema>;

export const SmartPricingConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.SMART_PRICING),
    failCostConstant: z.number().min(0).optional(),
    maxSteps: z.number().min(1).optional(),
    alphaPrior: z.number().min(0).optional(),
    betaPrior: z.number().min(0).optional(),
    timeDecay: z.number().min(0).max(1).optional(),
    offerSamplingRate: z.number().min(1).optional(),
    expectedOffersPerTruckToLoad: z.number().min(0).optional(),
    requiresApprovalMessage: z.string().optional(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type SmartPricingConfig = z.infer<typeof SmartPricingConfigSchema>;

export const AlwaysAcceptConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.ALWAYS_ACCEPT),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type AlwaysAcceptConfig = z.infer<typeof AlwaysAcceptConfigSchema>;

export const AlwaysDeclineConfigSchema = z
  .object({
    type: z.literal(NegotiationStrategyType.ALWAYS_DECLINE),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);
export type AlwaysDeclineConfig = z.infer<typeof AlwaysDeclineConfigSchema>;

export const NegotiationStrategyConfigSchema = z.discriminatedUnion('type', [
  ABTestConfigSchema,
  AcceptUpToMaxConfigSchema,
  AlwaysAcceptConfigSchema,
  AlwaysDeclineConfigSchema,
  ConditionalConfigSchema,
  EscalationConfigSchema,
  FixedAmountConfigSchema,
  NeverBudgeConfigSchema,
  PercentageBasedConfigSchema,
  SmartPricingConfigSchema,
]);

export type NegotiationStrategyConfig = z.infer<
  typeof NegotiationStrategyConfigSchema
>;

export const NegotiationStrategyConfigOverrideSchema = z
  .object({
    incrementAmount: z.number().optional(),
    incrementPercentage: z.number().min(0).max(100).optional(),
    initialIncrementAmount: z.number().optional(),
  })
  .merge(AutoRejectThresholdSchema)
  .merge(TargetBufferSchema)
  .merge(FirstOfferBelowTargetCounterSchema)
  .merge(CarrierNoBudgeBehaviorSchema);

export const NegotiationStrategyOverrideSchema = z.object({
  strategyId: z.string().uuid().optional(),
  strategyType: z.nativeEnum(NegotiationStrategyType).optional(),
  config: z
    .union([
      NegotiationStrategyConfigSchema,
      NegotiationStrategyConfigOverrideSchema,
    ])
    .optional(),
});

export type NegotiationStrategyOverride = z.infer<
  typeof NegotiationStrategyOverrideSchema
>;
